import type {
  RxDto,
  RxWriteDto,
  TherapyFrequencyDto,
  TherapyRxDto,
  TherapyRxWriteDto,
} from "../api-client";

/**
 * Convert an Rx the server RETURNED into the shape the server ACCEPTS (MD-232).
 *
 * The API used to send and accept the same class, so a loaded Rx could be posted
 * straight back. It no longer can: a returned line (`TherapyRxDto`) carries
 * resolved and server-computed data — `prescribedTherapy`, `scheduledCount`,
 * a back-reference to the `rx` — none of which is an input. A sent line
 * (`TherapyRxWriteDto`) names the term and nothing else.
 *
 * That is the point of the split, and this is the one place the conversion
 * happens: the editor loads an `RxDto`, mutates it, and hands it here on the way
 * out. Doing it in a helper rather than a cast means adding a write field is a
 * compile error here instead of a silently dropped value at runtime.
 *
 * Note `unitsPerDay` carries doubling. There is no `isDoubled` any more — a
 * Doppelbehandlung is `unitsPerDay = 2` on an ordinary line.
 */
/**
 * A line that names NO term cannot be saved: the server resolves
 * `rawText` -> alias -> term and rejects the whole request with
 * "Das Heilmittel \"unbekannt\" kann in der Tarifgruppe ... nicht zugeordnet
 * werden" — an error that says nothing about WHICH line or WHERE it was built.
 *
 * This has been the same bug four times over, each in a different builder or
 * loader that dropped the field. The warning fires at the point of
 * construction, naming the line, so the next one is found in the console
 * instead of by reading a 400 response.
 *
 * A warning, not a throw: `toRxWriteDto` also feeds live validation while the
 * user is still filling a row in, where a term-less line is expected and
 * transient.
 */
const warnIfNoTerm = (line: TherapyRxDto): void => {
  // E2E runs a PRODUCTION build, which is exactly the scenario that keeps
  // catching this — the warning has to survive it. `REACT_APP_E2E_AUTH_BRIDGE`
  // is compiled in only by the E2E runner (infrastructure/integration-tests/
  // scripts/run-e2e-local.mjs), so a real production bundle stays silent.
  const isE2EBuild = process.env.REACT_APP_E2E_AUTH_BRIDGE === "true";
  if (process.env.NODE_ENV === "production" && !isE2EBuild) return;
  if (line.rawText || line.prescribedTherapyId) return;
  // A blank row the user has not filled in yet is not interesting.
  if (!line.amount && !line.frequency) return;
  // eslint-disable-next-line no-console
  console.warn(
    "[MD-232] Prescription line names no prescribed term — it will be rejected " +
      'as \'Heilmittel "unbekannt"\'. Whatever built or loaded this row must ' +
      "carry `rawText` (or `prescribedTherapyId`); a resolved `therapy` object " +
      "is display-only and is not sent. Line:",
    line
  );
};

export const toTherapyRxWriteDto = (line: TherapyRxDto): TherapyRxWriteDto => (
  warnIfNoTerm(line),
  {
  // omitted when absent so the server treats the line as new
  ...(line.id ? { id: line.id } : {}),
  prescribedTherapyId: line.prescribedTherapyId ?? null,
  rawText: line.rawText ?? null,
  authority: line.authority,
  amount: line.amount ?? null,
  // `unitsPerDay` is the doubling input. Some form rows still carry only the
  // legacy `isDoubled` boolean; bridge it here so BOTH save channels (this one
  // and rxStore.normaliseRxForSave) agree — otherwise live validation and the
  // actual save would disagree about the same row.
  unitsPerDay: line.unitsPerDay ?? ((line as any).isDoubled ? 2 : 1),
  prescribedIntervals: line.prescribedIntervals ?? null,
  intervalDeviationReason: line.intervalDeviationReason ?? null,
  order: line.order,
  // Asserted, not defaulted. Every persisted line has a frequency (the column is
  // NOT NULL); the READ dto only marks it optional because `RxDto` marks
  // everything optional. Inventing one here would silently change capacity and
  // interval validation, so a genuinely missing frequency should fail the save.
  frequency: line.frequency as TherapyFrequencyDto,
}
);

/**
 * `RxDto` -> `RxWriteDto`. Every field except `therapyRxs` is identical between
 * the two, so the rest is spread through unchanged.
 */
export const toRxWriteDto = (rx: RxDto): RxWriteDto => {
  const { therapyRxs, ...rest } = rx;
  return {
    ...rest,
    therapyRxs: (therapyRxs ?? []).map(toTherapyRxWriteDto),
  } as RxWriteDto;
};
